import type { ReactNode } from "react";

import { cn } from "./lib/cn";

export interface TabItem {
  /** Kararlı kimlik — React anahtarı ve aktiflik karşılaştırması buradan. */
  key: string;
  label: string;
  /** Etiketin sağındaki sayaç ya da rozet. */
  badge?: ReactNode;
}

export interface TabsProps {
  items: readonly TabItem[];
  /** Aktif sekmenin `key`'i. Eşleşen yoksa hiçbiri aktif çizilmez. */
  activeKey: string | null;
  onSelect: (key: string) => void;
  /** Gezinme için erişilebilir ad — "Sözleşme bölümleri" gibi. */
  label: string;
  /**
   * `underline` (varsayılan) — mockup'ın standart alt gezinmesi (§5). `pill` — Aktivite
   * "Tümü/Açık/Sonuçlanan" gibi kompakt filtre grupları: muted zemin kutu, aktif sekme
   * surface + shadow-sm. Sözleşme detayının bölüm gezinmesi ("anchor modu") de görsel
   * olarak `underline`'dır — davranış farkı (kaydırma vs rota) çağıranın `onSelect`'inde,
   * bileşende yeni bir görsel değil.
   */
  variant?: "underline" | "pill";
  className?: string;
}

/**
 * Altı çizili sekme şeridi (onaylı görsel dil).
 *
 * ### Neden `NavItem` yetmedi
 *
 * `NavItem` bir HAP: dolgulu zemin, yuvarlak köşe. Kenar çubuğunda doğru okunuyor ama
 * sayfa içi alt gezinmede aynı ağırlıkta ikinci bir menü gibi duruyor ve kullanıcı hangi
 * seviyede olduğunu ayırt edemiyordu. Hedef tasarımlarda sayfa içi geçişler ince bir
 * çizgi şeridi — hiyerarşi görsel olarak ayrışıyor.
 *
 * ### Yatay taşma KAYBOLMAZ
 *
 * Şerit dar ekranda kendi içinde kayıyor (`overflow-x-auto`); sekmeleri gizlemek ya da
 * alta sarmak, sözleşme detayının sekiz bölümünden bir kısmını erişilemez yapardı.
 *
 * ### Bir GEZİNME, bir `tablist` DEĞİL
 *
 * Bölümler ayrı rotalar: tıklayınca sayfa değişiyor, panel değişmiyor. `role="tablist"`
 * yardımcı teknolojiye "bu sayfanın içinde panel değiştireceğim" sözü verirdi ve o söz
 * tutulmazdı. `<nav>` + `aria-current="page"` gerçekte olan biteni söylüyor.
 */
export function Tabs({
  items,
  activeKey,
  onSelect,
  label,
  variant = "underline",
  className,
}: TabsProps) {
  if (variant === "pill") {
    return (
      <nav aria-label={label} className={cn("overflow-x-auto", className)}>
        <ul className="flex min-w-max items-center gap-1 rounded-md bg-muted p-1">
          {items.map((item) => {
            const active = item.key === activeKey;
            return (
              <li key={item.key}>
                <button
                  type="button"
                  aria-current={active ? "page" : undefined}
                  onClick={() => onSelect(item.key)}
                  className={cn(
                    "flex h-9 items-center gap-2 rounded-md px-3.5 text-sm whitespace-nowrap transition-colors",
                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                    active
                      ? "bg-surface font-semibold text-fg-strong shadow-sm"
                      : "text-fg-muted hover:text-fg-heading",
                  )}
                >
                  {item.label}
                  {item.badge}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    );
  }

  return (
    <nav
      aria-label={label}
      className={cn("-mb-px overflow-x-auto border-b border-border-subtle", className)}
    >
      <ul className="flex h-11 min-w-max items-center gap-7">
        {items.map((item) => {
          const active = item.key === activeKey;
          return (
            <li key={item.key} className="h-full">
              <button
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => onSelect(item.key)}
                className={cn(
                  "flex h-full items-center gap-2 border-b-2 text-sm whitespace-nowrap transition-colors",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                  active
                    ? "border-primary font-semibold text-primary"
                    : "border-transparent font-medium text-fg-muted hover:border-border-strong hover:text-fg-heading",
                )}
              >
                {item.label}
                {item.badge}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
